import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, SeverityBadge, fmtDate, fmtDKK } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AiInsightsCard } from "@/components/AiInsightsCard";
import { ChartBox } from "@/components/ChartBox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  MapPin, CheckCircle2, Clock, Ban, Users, Award, UserRound, Dog, CalendarDays,
  CalendarCheck, Percent, ClipboardCheck, TrendingUp, Coins, Plus, GraduationCap,
  FileDown, ShieldCheck, AlertTriangle, ChevronRight, Activity,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const CHART_COLORS = ["hsl(28 33% 48%)", "hsl(24 40% 34%)", "hsl(38 45% 56%)", "hsl(30 18% 60%)", "hsl(200 14% 46%)"];
const cap = (s) => String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const KPIS = [
  { k: "total_locations", label: "Total Locations", icon: MapPin },
  { k: "active_locations", label: "Active", icon: CheckCircle2 },
  { k: "pending_locations", label: "Pending", icon: Clock },
  { k: "suspended_locations", label: "Suspended", icon: Ban },
  { k: "active_trainers", label: "Active Trainers", icon: Users },
  { k: "certified_trainers", label: "Certified", icon: Award },
  { k: "active_clients", label: "Active Clients", icon: UserRound },
  { k: "active_dogs", label: "Active Dogs", icon: Dog },
  { k: "upcoming_classes", label: "Upcoming Classes", icon: CalendarDays },
  { k: "completed_classes_month", label: "Completed (month)", icon: CalendarCheck },
  { k: "attendance_rate", label: "Attendance Rate", icon: Percent, suffix: "%" },
  { k: "homework_completion_rate", label: "Homework Rate", icon: ClipboardCheck, suffix: "%" },
];

const HEALTH_DOT = { healthy: "bg-green-500", warning: "bg-amber-500", critical: "bg-red-500" };
const SEV_STYLE = {
  critical: "border-red-200 bg-red-50",
  warning: "border-amber-200 bg-amber-50",
  info: "border-border bg-muted/40",
};

export default function HeadOffice() {
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [locs, setLocs] = useState([]);
  const [attention, setAttention] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    api.get("/network/overview").then((r) => setOv(r.data)).catch(() => {});
    api.get("/network/locations").then((r) => setLocs(r.data)).catch(() => {});
    api.get("/network/attention").then((r) => setAttention(r.data)).catch(() => {});
    api.get("/network/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);

  const QUICK = [
    { label: "Add Location", icon: Plus, onClick: () => navigate("/locations"), testid: "qa-add-location" },
    { label: "Add Trainer", icon: Users, onClick: () => navigate("/trainers"), testid: "qa-add-trainer" },
    { label: "Add Program", icon: GraduationCap, onClick: () => navigate("/programs"), testid: "qa-add-program" },
    { label: "Attention", icon: AlertTriangle, onClick: () => navigate("/network-health"), testid: "qa-attention" },
    { label: "Export Report", icon: FileDown, onClick: () => toast.info("Export coming with HV Analytics"), testid: "qa-export" },
    { label: "Quality Review", icon: ShieldCheck, onClick: () => toast.info("Quality review coming with HV Quality"), testid: "qa-quality" },
  ];

  return (
    <div>
      {/* Quick actions */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="ho-quick-actions">
        {QUICK.map((a) => (
          <Button key={a.label} variant="outline" size="sm" className="gap-2" onClick={a.onClick} data-testid={a.testid}>
            <a.icon size={15} strokeWidth={1.5} /> {a.label}
          </Button>
        ))}
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
        {KPIS.map((k) => (
          <Card key={k.k} className="border-border" data-testid={`ho-kpi-${k.k}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <k.icon size={15} strokeWidth={1.5} />
                <span className="text-[11px] uppercase tracking-wide font-semibold">{k.label}</span>
              </div>
              <div className="text-2xl font-heading font-semibold tracking-tight">
                {ov ? ov[k.k] : "—"}{k.suffix && ov ? k.suffix : ""}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Revenue + Royalty */}
      <div className="mb-6"><AiInsightsCard /></div>
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <Card className="border-border bg-primary text-primary-foreground">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-primary-foreground/70 text-xs uppercase tracking-wide font-semibold"><TrendingUp size={14} /> Network Revenue (placeholder)</div>
              <div className="text-3xl font-heading font-semibold mt-1.5">{ov ? fmtDKK(ov.revenue_placeholder) : "—"}</div>
              <div className="text-primary-foreground/70 text-xs mt-1">From {ov?.total_bookings ?? 0} bookings · full billing with HV Payments</div>
            </div>
            <TrendingUp size={40} className="text-primary-foreground/40 hidden sm:block" strokeWidth={1.5} />
          </CardContent>
        </Card>
        <Card className="border-border bg-neutral-900 text-white">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wide font-semibold"><Coins size={14} /> Franchise Royalty (placeholder)</div>
              <div className="text-3xl font-heading font-semibold mt-1.5">{ov ? fmtDKK(ov.royalty_placeholder) : "—"}</div>
              <div className="text-white/60 text-xs mt-1">Est. 7% · full royalty engine coming soon</div>
            </div>
            <Coins size={40} className="text-white/30 hidden sm:block" strokeWidth={1.5} />
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* Location health */}
        <Card className="border-border lg:col-span-2 overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2"><Activity size={18} strokeWidth={1.5} /> Location Health</CardTitle>
            <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground" onClick={() => navigate("/network-health")} data-testid="ho-view-all-locations">View all <ChevronRight size={14} /></Button>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid="ho-location-table">
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  {["Location", "Status", "Trainers", "Dogs", "Attend.", "HW", "Quality", "Health"].map((h) => (
                    <TableHead key={h} className="text-xs uppercase tracking-wide font-semibold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {locs.map((l) => (
                  <TableRow key={l.id} className="hover:bg-muted/40 cursor-pointer" onClick={() => navigate(`/locations/${l.id}`)} data-testid="ho-location-row">
                    <TableCell>
                      <div className="font-medium">{l.name}</div>
                      <div className="text-xs text-muted-foreground">{l.city}, {l.country}</div>
                    </TableCell>
                    <TableCell><StatusBadge value={l.status} /></TableCell>
                    <TableCell>{l.active_trainers}</TableCell>
                    <TableCell>{l.active_dogs}</TableCell>
                    <TableCell>{l.attendance_rate}%</TableCell>
                    <TableCell>{l.homework_completion_rate}%</TableCell>
                    <TableCell><span className="font-medium">{l.quality_score}</span></TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-xs capitalize">
                        <span className={`h-2 w-2 rounded-full ${HEALTH_DOT[l.attention]}`} /> {l.attention}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
                {locs.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No locations.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Attention center */}
        <Card className="border-border">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><AlertTriangle size={18} strokeWidth={1.5} className="text-amber-600" /> Attention Center</CardTitle></CardHeader>
          <CardContent className="space-y-2 max-h-[420px] overflow-y-auto">
            {attention.length ? attention.map((a) => (
              <div key={`${a.category}-${a.title}`} className={`p-3 rounded-lg border ${SEV_STYLE[a.severity]}`} data-testid="ho-attention-item">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">{a.category}</span>
                  <SeverityBadge value={a.severity} />
                </div>
                <div className="text-sm font-medium mt-1">{a.title}</div>
                {a.detail && <div className="text-xs text-muted-foreground mt-0.5">{a.detail}</div>}
              </div>
            )) : <p className="text-sm text-muted-foreground py-6 text-center">Network is healthy 🎉</p>}
          </CardContent>
        </Card>
      </div>

      {/* Network analytics */}
      <div className="grid md:grid-cols-2 gap-6">
        <ChartCard title="Dogs by Location" data={analytics?.dogs_by_location} type="bar" />
        <ChartCard title="Classes by Location" data={analytics?.classes_by_location} type="bar" />
        <ChartCard title="Trainer Certification" data={analytics?.trainers_by_certification?.map((d) => ({ name: cap(d.name), value: d.value }))} type="pie" />
        <ChartCard title="Dogs by Training Level" data={analytics?.dogs_by_level?.map((d) => ({ name: cap(d.name), value: d.value }))} type="bar" />
      </div>
    </div>
  );
}

function ChartCard({ title, data, type }) {
  return (
    <Card className="border-border">
      <CardHeader><CardTitle className="font-heading text-lg">{title}</CardTitle></CardHeader>
      <CardContent>
        <ChartBox height={256}>
          {type === "pie" ? (
            <PieChart>
              <Pie data={data || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {(data || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          ) : (
            <BarChart data={data || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(34 22% 87%)" vertical={false} />
              <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: "hsl(34 24% 91%)" }} />
              <Bar dataKey="value" fill="hsl(28 33% 48%)" radius={[6, 6, 0, 0]} />
            </BarChart>
          )}
        </ChartBox>
      </CardContent>
    </Card>
  );
}
